import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { UserTableViewsContext } from './UserTableSettingsContext';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ApiService } from '@/support/ApiService';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { selectTeam } from '@/reducers/team';
import type { State } from '@/model/State.model';
import type {
  UserTableView,
  UserTableViewApiId,
  UserTableViewsByTable,
  UserTableViewSettings,
  UserTableViewTableName,
} from '@/model/UserTableView.model.ts';

/**
 * Provider that manages user table views with server persistence.
 * Fetches all views on mount, provides methods to create/delete/activate views.
 */
export const UserTableViewsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { newAbortController } = useAbortController();

  // Watch for user/team changes to reset and refetch
  const team = useSelector(selectTeam);
  const currentUser = useSelector((state: State) => state.user.currentUser);
  const isLoggedIn = Boolean(window.localStorage.getItem('token'));
  const disabled = !isLoggedIn || team == null || currentUser == null;

  // Fetch views using useApiHook - manual invoke only
  const {
    loading: isLoading,
    response: fetchResponse,
    invoke,
  } = useApiHook({
    endpoint: 'GET /api/v1/user-table-views',
    invokeOnInit: false,
    options: {
      onError: { throw: false, toast: false, capture: false },
    },
  });

  // Tables state - null means not loaded yet, empty object means loaded with no views
  const [tables, setTables] = useState<Record<string, UserTableViewsByTable> | null>(null);

  // Refetch when user or team changes, clear when logged out
  const invokeRef = useRef(invoke);
  useEffect(() => {
    invokeRef.current = invoke;
  }, [invoke]);

  useEffect(() => {
    if (!disabled) {
      invokeRef.current();
    } else {
      setTables(null);
    }
  }, [disabled, currentUser?.id, team?.id]);

  useEffect(() => {
    if (!disabled && fetchResponse?.type === 'success') {
      const body = fetchResponse.body as { data?: Record<string, UserTableViewsByTable> };
      setTables(body?.data ?? {});
    } else if (!disabled && fetchResponse?.type === 'error') {
      setTables({});
    }
  }, [fetchResponse, disabled]);

  const isLoaded = !disabled && tables !== null;
  const error = !disabled && fetchResponse?.type === 'error' ? fetchResponse.error : null;

  const refetch = useCallback(async (): Promise<void> => {
    if (!disabled) {
      await invoke();
    }
  }, [invoke, disabled]);

  const getActiveSettings = useCallback(
    (tableName: string): UserTableViewSettings | undefined => {
      if (tables === null) {
        return undefined;
      }
      const tableData = tables[tableName];
      if (!tableData?.active_view_api_id) {
        return undefined;
      }
      const activeView = tableData.views.find((v) => v.api_id === tableData.active_view_api_id);
      return activeView?.settings;
    },
    [tables]
  );

  const getViews = useCallback(
    (tableName: string): Array<UserTableView> => {
      if (tables === null) {
        return [];
      }
      const views = tables[tableName]?.views ?? [];
      return [...views].sort((a, b) => a.name.localeCompare(b.name));
    },
    [tables]
  );

  const getActiveViewApiId = useCallback(
    (tableName: string): UserTableViewApiId | undefined => {
      if (tables === null) {
        return undefined;
      }
      return tables[tableName]?.active_view_api_id ?? undefined;
    },
    [tables]
  );

  const createView = useCallback(
    async (
      tableName: UserTableViewTableName,
      name: string,
      settings: UserTableViewSettings,
      setActive = false
    ): Promise<UserTableView | null> => {
      try {
        const response = await ApiService.call({
          endpoint: 'POST /api/v1/user-table-views',
          body: {
            table_name: tableName,
            name,
            settings,
            set_active: setActive,
          },
          signal: newAbortController().signal,
          options: { onError: { throw: false, toast: false } },
        });

        if (response.type === 'success') {
          const newView = response.body as UserTableView;
          setTables((prev) => {
            if (prev === null) {
              return prev;
            }
            const existingTable = prev[tableName] ?? { views: [] };
            return {
              ...prev,
              [tableName]: {
                views: [...existingTable.views, newView],
                active_view_api_id: setActive ? newView.api_id : existingTable.active_view_api_id,
              },
            };
          });
          return newView;
        }
        return null;
      } catch (error) {
        if (notAborted(error)) {
          ExceptionHandler.captureException(
            new InVivoError('Could not create table view', { cause: error, slug: 'user-table-views-create' })
          );
        }
        return null;
      }
    },
    [newAbortController]
  );

  const deleteView = useCallback(
    async (viewApiId: UserTableViewApiId): Promise<boolean> => {
      try {
        const response = await ApiService.call({
          endpoint: 'DELETE /api/v1/user-table-views/{viewApiId}',
          path: { viewApiId },
          signal: newAbortController().signal,
          options: { onError: { throw: false, toast: false } },
        });

        if (response.type === 'success') {
          setTables((prev) => {
            if (prev === null) {
              return prev;
            }
            const newTables: Record<string, UserTableViewsByTable> = {};
            for (const [tableName, tableData] of Object.entries(prev)) {
              const filteredViews = tableData.views.filter((v) => v.api_id !== viewApiId);
              const wasActive = tableData.active_view_api_id === viewApiId;
              if (filteredViews.length > 0 || !wasActive) {
                newTables[tableName] = {
                  views: filteredViews,
                  active_view_api_id: wasActive ? undefined : tableData.active_view_api_id,
                };
              }
            }
            return newTables;
          });
          return true;
        }
        return false;
      } catch (error) {
        if (notAborted(error)) {
          ExceptionHandler.captureException(
            new InVivoError('Could not delete table view', { cause: error, slug: 'user-table-views-delete' })
          );
        }
        return false;
      }
    },
    [newAbortController]
  );

  const setActiveView = useCallback(
    async (tableName: UserTableViewTableName, viewApiId: UserTableViewApiId): Promise<boolean> => {
      try {
        const response = await ApiService.call({
          endpoint: 'PATCH /api/v1/user-table-views/active',
          body: {
            table_name: tableName,
            view_api_id: viewApiId,
          },
          signal: newAbortController().signal,
          options: { onError: { throw: false, toast: false } },
        });

        if (response.type === 'success') {
          setTables((prev) => {
            if (prev === null) {
              return prev;
            }
            const existingTable = prev[tableName];
            if (!existingTable) {
              return prev;
            }
            return {
              ...prev,
              [tableName]: {
                ...existingTable,
                active_view_api_id: viewApiId,
              },
            };
          });
          return true;
        }
        return false;
      } catch (error) {
        if (notAborted(error)) {
          ExceptionHandler.captureException(
            new InVivoError('Could not set active table view', { cause: error, slug: 'user-table-views-set-active' })
          );
        }
        return false;
      }
    },
    [newAbortController]
  );

  const clearActiveView = useCallback(
    async (tableName: UserTableViewTableName): Promise<boolean> => {
      try {
        const response = await ApiService.call({
          endpoint: 'DELETE /api/v1/user-table-views/active',
          query: { table_name: tableName },
          signal: newAbortController().signal,
          options: { onError: { throw: false, toast: false } },
        });

        if (response.type === 'success') {
          setTables((prev) => {
            if (prev === null) {
              return prev;
            }
            const existingTable = prev[tableName];
            if (!existingTable) {
              return prev;
            }
            return {
              ...prev,
              [tableName]: {
                ...existingTable,
                active_view_api_id: undefined,
              },
            };
          });
          return true;
        }
        return false;
      } catch (error) {
        if (notAborted(error)) {
          ExceptionHandler.captureException(
            new InVivoError('Could not clear active table view', {
              cause: error,
              slug: 'user-table-views-clear-active',
            })
          );
        }
        return false;
      }
    },
    [newAbortController]
  );

  const contextValue = useMemo(
    () => ({
      isLoading,
      isLoaded,
      error,
      getActiveSettings,
      getViews,
      getActiveViewApiId,
      createView,
      deleteView,
      setActiveView,
      clearActiveView,
      refetch,
    }),
    [
      isLoading,
      isLoaded,
      error,
      getActiveSettings,
      getViews,
      getActiveViewApiId,
      createView,
      deleteView,
      setActiveView,
      clearActiveView,
      refetch,
    ]
  );

  return <UserTableViewsContext.Provider value={contextValue}>{children}</UserTableViewsContext.Provider>;
};
